import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
const DEACTIVATE_DELAY_WITH_INTENT = 600;
const DEACTIVATE_DELAY_QUICK = 150;
const INTENT_Y_THRESHOLD = 4;
const INTENT_X_THRESHOLD = 16;
const HISTORY_SIZE = 3;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = [];

  #abortController = new AbortController();
  #mouseHistory = [];
  #deactivateTimeoutId = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#scheduleDeactivate(DEACTIVATE_DELAY), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('pointermove', this.#trackMouseMove, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('pointerover', this.#handleCollectionPreview, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('focusin', this.#handleCollectionPreview, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#clearDeactivateTimer();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#clearDeactivateTimer();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      const intent =
        event instanceof PointerEvent ? this.#getMouseIntent() : 'neutral';
      let delay = DEACTIVATE_DELAY;
      if (intent === 'towards-submenu') {
        delay = DEACTIVATE_DELAY_WITH_INTENT;
      } else if (intent === 'away') {
        delay = DEACTIVATE_DELAY_QUICK;
      }
      this.#scheduleDeactivate(delay);
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #clearDeactivateTimer = () => {
    if (this.#deactivateTimeoutId != null) {
      clearTimeout(this.#deactivateTimeoutId);
      this.#deactivateTimeoutId = null;
    }
  };

  #scheduleDeactivate = (delay) => {
    this.#clearDeactivateTimer();
    this.#deactivateTimeoutId = setTimeout(() => {
      this.#deactivate();
    }, delay);
  };

  #trackMouseMove = (event) => {
    if (!(event instanceof PointerEvent)) return;
    this.#mouseHistory.push({ x: event.clientX, y: event.clientY });
    if (this.#mouseHistory.length > HISTORY_SIZE) {
      this.#mouseHistory.shift();
    }
  };

  #getMouseIntent = () => {
    if (this.#mouseHistory.length < 2) return 'neutral';
    const first = this.#mouseHistory[0];
    const last = this.#mouseHistory[this.#mouseHistory.length - 1];
    const deltaX = last.x - first.x;
    const deltaY = last.y - first.y;

    if (deltaY > INTENT_Y_THRESHOLD) {
      if (Math.abs(deltaX) <= INTENT_X_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return 'towards-submenu';
      }
    }

    if (Math.abs(deltaX) > INTENT_X_THRESHOLD || deltaY <= INTENT_Y_THRESHOLD) {
      return 'away';
    }

    return 'neutral';
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #handleCollectionPreview = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('[data-collection-handle]');
    if (!link) {
      return;
    }

    const handle = link.dataset.collectionHandle;
    if (!handle) {
      return;
    }

    const megaMenu = link.closest('.mega-menu');
    if (!megaMenu) {
      return;
    }

    const contentList = megaMenu.querySelector('[data-collection-hover-list]');
    if (!contentList) {
      return;
    }

    this.#setActiveCollectionPanel(contentList, handle);
  };

  #setActiveCollectionPanel = (contentList, handle) => {
    if (!(contentList instanceof HTMLElement)) return;
    if (contentList.dataset.activeCollectionHandle === handle) return;

    contentList.dataset.activeCollectionHandle = handle;

    const panels = contentList.querySelectorAll('[data-collection-panel]');
    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const panelHandle = panel.getAttribute('data-collection-panel');
      const isActive = panelHandle === handle;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
